import {
  ActivityFilterKey,
  FeatureName,
  useActivityListFilter,
  useIsFeatureEnabled,
  useSetActivityListFilter,
} from '@gripp/shared-logic';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet } from 'react-native';
import { SearchInput } from '../crop-circle/Components/SearchInput';
import { colors as CropCircleColors } from '../crop-circle/tokens';
import { DebouncedTextInput } from '../forms';

type ActivitySearchInputProps = {
  filterKey?: ActivityFilterKey.AssetActivity | ActivityFilterKey.Activity;
  onBlur?: () => void;
  autoFocus?: boolean;
};

export const ActivitySearchInput: React.FC<ActivitySearchInputProps> = ({
  filterKey = ActivityFilterKey.Activity,
  onBlur,
  autoFocus = false,
}) => {
  const { t } = useTranslation();
  const isCropCircleRedesign = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  const { data: activityFilter, isLoading } = useActivityListFilter(filterKey);

  const { mutateAsync } = useSetActivityListFilter(filterKey);

  const [text, setText] = useState<string>(activityFilter?.keyword ?? '');
  // Gate mutation firing until the user actually types. Prevents the
  // inner SearchInput's mount effect (which fires onDebounce('') when
  // value is initially empty) from clobbering the persisted keyword
  // during navigation between pages that share this filter.
  const hasTyped = useRef(false);

  useEffect(() => {
    if (!text && activityFilter?.keyword) {
      setText(activityFilter.keyword);
    }
  }, [activityFilter]);

  const updateSearch = useCallback(
    async (keyword: string) => {
      if (!hasTyped.current) return;
      activityFilter.keyword = keyword;
      await mutateAsync(activityFilter);
    },
    [activityFilter, mutateAsync]
  );

  const handleChangeText = useCallback((value: string) => {
    if (!hasTyped.current) hasTyped.current = true;
    setText(value);
  }, []);

  if (!activityFilter) return null;

  if (isCropCircleRedesign) {
    return (
      <SearchInput
        value={text}
        onChangeText={handleChangeText}
        onDebounce={updateSearch}
        placeholder={t('activity.filter.searchPlaceholder')}
        autoFocus={autoFocus}
        width={'100%'}
        hideSearchIcon
        hideClearButton
        onBlur={() => {
          if (!text && onBlur) onBlur();
        }}
      />
    );
  }

  return (
    <DebouncedTextInput
      debounceTimeMs={500}
      debounceFunc={updateSearch}
      placeholder={t('activity.filter.searchPlaceholder')}
      value={text}
      onBlur={() => {
        if (!text && onBlur) onBlur();
      }}
      onChangeText={handleChangeText}
      height={44}
      width={'100%'}
      outlineStyle={{
        ...styles.inputStyle,
        backgroundColor:
          Platform.OS !== 'web' ? CropCircleColors.neutral[200] : 'white',
      }}
      disableInnerStyle={true}
      autoFocus={autoFocus}
    />
  );
};

const styles = StyleSheet.create({
  inputStyle: {
    backgroundColor: CropCircleColors.neutral[200],
    borderWidth: 0,
    borderRadius: 8,
  },
});
